import React, { useState, useEffect, useRef } from 'react';
import {
  Home, BookOpen, Heart, Briefcase, MessageCircle, MessageSquare, Shield,
  ChevronDown, LogOut, Settings, User
} from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useChatCtaEnabled } from '../../hooks/useChatCtaEnabled';
import { useUnreadConversationCount, ensureConversationsLoaded } from '../lib/chat';
import CurrencyToggle from '../../components/CurrencyToggle';
import { MyPurchaseNavLink } from '../../components/assist/room/MyPurchase';

interface UserData {
  id: string;
  email: string;
  name: string;
  role: 'buyer' | 'agent' | 'admin';
  avatarUrl?: string;
  tier?: string;
}

/**
 * SSR-safe Header component matching Navbar.tsx functionality.
 * Uses <a> tags instead of react-router-dom <Link>.
 * Self-contained auth check via supabase on mount.
 *
 * Includes full navigation:
 * - Discover, Blog (all users)
 * - Dashboard, Leads (agents)
 * - Saved, Messages (buyers)
 * - Admin (admins)
 * - Profile dropdown with avatar
 */
interface HeaderSSRProps {
  /**
   * CURR-01: show the KES/USD display-currency toggle. Opt-in, because this
   * header is shared by ~20 pages and the toggle belongs only on the buyer
   * surfaces (Home, Property Details, search results). Off everywhere else.
   */
  showCurrencyToggle?: boolean;
  /**
   * HOME-01 fix round 1 — widen the header's content column to the Home
   * surface's own 1440px shell.
   *
   * Home's rows are centred at 1440 with 64px gutters; this header is centred
   * at `max-w-7xl` (1280) with different gutters, so on a wide monitor the
   * logo sat visibly inside the left edge of the hero card beneath it. Opt-in
   * rather than changed globally: every other page in the product is laid out
   * against 1280, and widening the shared header for all of them is a
   * site-wide layout change, not a Home fix.
   */
  wide?: boolean;
}

export default function HeaderSSR({
  showCurrencyToggle = false,
  wide = false,
}: HeaderSSRProps = {}) {
  const [user, setUser] = useState<UserData | null>(null);
  const [newLeadsCount, setNewLeadsCount] = useState(0);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // CHAT-17: resolved chat flag (DB flag OR CHAT-16 preview override). Stays
  // false while in flight, so the OFF leads behaviour renders unchanged.
  const chatOn = useChatCtaEnabled() === true;
  const unreadConversations = useUnreadConversationCount();

  // Seed the shared conversations cache backing the reactive chat-unread badge.
  useEffect(() => {
    if (chatOn && user?.id) {
      ensureConversationsLoaded(user.id);
    }
  }, [chatOn, user?.id]);

  // Fetch user data on mount (client-side only)
  useEffect(() => {
    if (typeof window === 'undefined') return;

    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) return;

        const authUser = session.user;

        // Fetch profile from database for canonical role, name, avatar.
        // maybeSingle() returns data=null without an error when the row is
        // RLS-hidden — that's the signal we use for soft-deleted accounts.
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('full_name, avatar_url, role, tier')
          .eq('id', authUser.id)
          .maybeSingle();

        // Soft-deleted account: profile row exists but RLS hides it from the
        // owner. We never default to role='buyer' here — that was the bug
        // that let deleted users browse as buyers. PGRST116 is "not found";
        // any other error is treated as transient and the header stays
        // unauthenticated rather than triggering a destructive sign-out.
        const noRow =
          !profile && (!profileError || profileError.code === 'PGRST116');
        if (noRow) {
          const createdAt = authUser.created_at
            ? new Date(authUser.created_at).getTime()
            : 0;
          const isBrandNew =
            createdAt > 0 && Date.now() - createdAt < 60_000;
          if (!isBrandNew) {
            await supabase.auth.signOut();
            window.location.replace('/auth?account_deleted=1');
            return;
          }
          // Brand-new user — let AuthContext's profile creation race finish.
          return;
        }
        if (!profile) {
          // Real error from PostgREST — leave header unauthenticated rather
          // than risk an incorrect sign-out.
          return;
        }

        const metadata = authUser.user_metadata || {};
        const name = profile.full_name || metadata.full_name || metadata.name || authUser.email?.split('@')[0] || 'User';
        const role = (profile.role || metadata.role || 'buyer') as UserData['role'];

        const userData: UserData = {
          id: authUser.id,
          email: authUser.email || '',
          name,
          role,
          avatarUrl: profile.avatar_url || metadata.avatar_url || metadata.avatarUrl,
          tier: profile.tier || metadata.tier,
        };

        setUser(userData);

        // Fetch lead count for agents
        if (userData.role === 'agent') {
          try {
            if (session.access_token) {
              const response = await fetch('/api/leads-teaser', {
                headers: { 'Authorization': `Bearer ${session.access_token}` }
              });
              if (response.ok) {
                const teaser = await response.json();
                setNewLeadsCount(teaser.newCount || 0);
              }
            }
          } catch {
            // Silently fail - badge just won't show
          }
        }
      } catch {
        // Not logged in
      }
    })();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setIsDropdownOpen(false);
    window.location.href = '/';
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    // Navbar safe-area: mirror Navbar.tsx — grow the fixed header by the top
    // safe-area inset so the logo clears the status bar on native (env()=0 on web).
    <nav className="fixed top-0 left-0 right-0 z-50 bg-brand-bg/90 backdrop-blur-xl border-b border-brand-border transition-all duration-300 h-[calc(4rem+env(safe-area-inset-top))] pt-[env(safe-area-inset-top)]">
      <div
        className={
          wide
            ? 'mx-auto h-full w-full max-w-[1440px] px-[18px] lg:px-16'
            : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full'
        }
      >
        <div className="flex justify-between items-center h-full">

          {/* Logo */}
          <a href="/" className="flex items-center group">
            <img
              src="/afriqahome-logo.svg"
              alt="Afriqahome"
              width={160}
              height={40}
              className="h-10 w-auto"
            />
          </a>

          {/* CURR-01: display-currency toggle. Outside the `hidden md:flex`
              desktop nav so it is reachable on mobile too — diaspora buyers are
              disproportionately on phones. Renders nothing when the rate is
              missing or stale. */}
          {showCurrencyToggle && <CurrencyToggle />}

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center space-x-8">
            {/* Always visible */}
            <NavLink href="/" icon={<Home size={18} />} label="Discover" />
            <NavLink href="/guides" icon={<BookOpen size={18} />} label="Guides" />

            {/* Buyer-specific */}
            {user?.role === 'buyer' && (
              <>
                <NavLink href="/saved" icon={<Heart size={18} />} label="Saved" />
                <a
                  href="/messages"
                  className="flex items-center space-x-2 text-sm font-medium text-gray-500 hover:text-brand-primary transition-colors duration-200"
                >
                  <MessageSquare size={18} />
                  <span>Messages</span>
                  {chatOn && <UnreadBadge count={unreadConversations} />}
                </a>
              </>
            )}

            {/* ASSIST-05: client-only, after get_my_active_case returns an open case. */}
            <MyPurchaseNavLink
              viewerId={user?.id}
              anchor
              className="flex items-center space-x-2 text-sm font-medium text-gray-500 hover:text-brand-primary transition-colors duration-200"
            />

            {/* Agent-specific */}
            {user?.role === 'agent' && (
              <>
                <NavLink href="/agent" icon={<Briefcase size={18} />} label="Dashboard" />
                <a
                  href="/messages"
                  className="flex items-center space-x-2 text-sm font-medium text-gray-500 hover:text-brand-primary transition-colors duration-200"
                >
                  {chatOn ? <MessageSquare size={18} /> : <MessageCircle size={18} />}
                  <span>{chatOn ? 'Messages' : 'Leads'}</span>
                  {chatOn
                    ? <UnreadBadge count={unreadConversations} />
                    : newLeadsCount > 0 && (
                        <span className="bg-brand-accent text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center animate-pulse">
                          {newLeadsCount > 9 ? '9+' : newLeadsCount}
                        </span>
                      )}
                </a>
              </>
            )}

            {/* Admin-specific */}
            {user?.role === 'admin' && (
              <NavLink href="/admin" icon={<Shield size={18} />} label="Admin" />
            )}

            <div className="w-px h-6 bg-brand-border mx-2" />

            {/* User State */}
            {user ? (
              <div className="relative" ref={dropdownRef}>
                {/* Trigger Button */}
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="flex items-center space-x-2 p-2 rounded-xl hover:bg-gray-100 transition-colors"
                >
                  {/* Avatar */}
                  <div className="w-9 h-9 rounded-full bg-brand-accent flex items-center justify-center text-white font-semibold text-sm overflow-hidden">
                    {user.avatarUrl ? (
                      <img
                        src={user.avatarUrl}
                        alt={user.name}
                        className="w-9 h-9 rounded-full object-cover"
                      />
                    ) : (
                      getInitials(user.name)
                    )}
                  </div>

                  {/* Name & Role */}
                  <div className="hidden sm:block text-left">
                    <p className="text-sm font-semibold text-gray-900 leading-tight">{user.name}</p>
                    <p className="text-xs text-gray-500 capitalize">{user.role}</p>
                  </div>

                  {/* Chevron */}
                  <ChevronDown
                    size={16}
                    className={`text-gray-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {/* Dropdown Menu */}
                {isDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-lg border border-gray-100 py-2 z-50">
                    {/* User Info Header */}
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-semibold text-gray-900">{user.name}</p>
                      <p className="text-xs text-gray-500 truncate">{user.email}</p>
                      {user.role === 'agent' && user.tier && (
                        <span className="inline-block mt-1 px-2 py-0.5 bg-brand-accent/10 text-brand-accent text-xs font-medium rounded-full capitalize">
                          {user.tier} Plan
                        </span>
                      )}
                    </div>

                    {/* Menu Items */}
                    <div className="py-2">
                      {user.role === 'buyer' && (
                        <>
                          <DropdownLink href="/profile" icon={<Settings size={18} />} label="Settings" onClick={() => setIsDropdownOpen(false)} />
                          <DropdownLink href="/saved" icon={<Heart size={18} />} label="Saved Homes" onClick={() => setIsDropdownOpen(false)} />
                        </>
                      )}
                      {user.role === 'agent' && (
                        <DropdownLink href="/agent" icon={<Briefcase size={18} />} label="Agent Dashboard" onClick={() => setIsDropdownOpen(false)} />
                      )}
                      {user.role === 'admin' && (
                        <>
                          <DropdownLink href="/admin" icon={<Shield size={18} />} label="Admin Console" onClick={() => setIsDropdownOpen(false)} />
                          <DropdownLink href="/profile" icon={<Settings size={18} />} label="Settings" onClick={() => setIsDropdownOpen(false)} />
                        </>
                      )}
                    </div>

                    {/* Logout */}
                    <div className="border-t border-gray-100 pt-2">
                      <button
                        onClick={handleSignOut}
                        className="flex items-center space-x-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 w-full transition-colors"
                      >
                        <LogOut size={18} />
                        <span>Sign Out</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <a
                href="/auth"
                className="px-5 py-2 bg-brand-primary text-white text-sm font-semibold rounded-full hover:bg-brand-primary/90 transition-all shadow-sm"
              >
                Sign In
              </a>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

// CHAT-17: reactive chat-unread badge — hidden at zero, capped at "9+",
// matching the existing leads-badge size/style.
function UnreadBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span className="bg-brand-accent text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center animate-pulse">
      {count > 9 ? '9+' : count}
    </span>
  );
}

// SSR-safe NavLink using <a> tag
function NavLink({ href, icon, label }: { href: string; icon: React.ReactNode; label: string }) {
  return (
    <a
      href={href}
      className="flex items-center space-x-2 text-sm font-medium text-gray-500 hover:text-brand-primary transition-colors duration-200"
    >
      {icon}
      <span>{label}</span>
    </a>
  );
}

// Dropdown menu link
function DropdownLink({ href, icon, label, onClick }: { href: string; icon: React.ReactNode; label: string; onClick: () => void }) {
  return (
    <a
      href={href}
      onClick={onClick}
      className="flex items-center space-x-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
    >
      <span className="text-gray-400">{icon}</span>
      <span>{label}</span>
    </a>
  );
}
